import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import adminApi from '../api/client';

// ── Types ───────────────────────────────────────────────────────────────────

interface Author {
  id: string;
  name: string;
  bio: string;
  avatar_url: string;
  is_claimed: boolean;
  claimed_by_user_id: string;
  verification_status: string;
  created_at: string;
  claimer_name: string;
  claimer_email: string;
}

interface AllAuthorsResponse {
  total: number;
  page: number;
  limit: number;
  authors: Author[];
}

interface Claim {
  claim_id: string;
  user_id: string;
  claimer_name: string | null;
  claimer_email: string | null;
  author_id: string | null;
  author_name: string | null;
  author_name_sinhala: string | null;
  author_is_verified: boolean;
  is_new_author_request: boolean;
  status: string;
  role: string;
  pen_name: string | null;
  evidence: Record<string, any>;
  submitted_at: string | null;
  reviewed_at: string | null;
  review_reason: string | null;
}

interface ClaimsResponse {
  claims: Claim[];
  total: number;
  page: number;
  limit: number;
}

interface ClaimDetail {
  claim_id: string;
  status: string;
  role: string;
  pen_name: string | null;
  is_new_author_request: boolean;
  proposed_name: string | null;
  proposed_name_sinhala: string | null;
  evidence: Record<string, any>;
  submitted_at: string | null;
  reviewed_at: string | null;
  review_reason: string | null;
  claimer: { id: string | null; name: string | null; email: string | null };
  author: { id: string; name: string; name_sinhala: string | null; avatar_url: string | null; is_verified: boolean; is_active: boolean; book_count: number } | null;
  similar_authors: { id: string; name: string; name_sinhala: string | null; is_verified: boolean }[];
  warnings: string[];
}

// ── Shared Components ───────────────────────────────────────────────────────

const Toast = ({ message, type, onClose }: { message: string; type: 'success' | 'error'; onClose: () => void }) => {
  useEffect(() => {
    if (message) {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }
  }, [message, onClose]);
  if (!message) return null;
  return (
    <div className={`fixed bottom-4 right-4 px-6 py-3 rounded shadow-lg text-white font-medium z-50 ${type === 'success' ? 'bg-[#1D9E75]' : 'bg-[#D85A30]'}`}>
      {message}
    </div>
  );
};

const ReasonModal = ({ isOpen, onClose, onConfirm, title, placeholder, confirmLabel, confirmColor }: {
  isOpen: boolean; onClose: () => void; onConfirm: (reason: string) => void;
  title: string; placeholder: string; confirmLabel: string; confirmColor: string;
}) => {
  const [reason, setReason] = useState("");
  useEffect(() => { if (isOpen) setReason(""); }, [isOpen]);
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
        <h3 className="text-xl font-bold text-[#085041] mb-4">{title}</h3>
        <textarea
          className="w-full border border-gray-300 rounded p-3 mb-4 focus:outline-none focus:border-[#1D9E75] min-h-[120px] resize-none"
          placeholder={placeholder}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded font-medium">Cancel</button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={!reason.trim()}
            className={`px-4 py-2 text-white rounded hover:opacity-90 disabled:opacity-50 font-medium`}
            style={{ backgroundColor: confirmColor }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const ClaimStatusBadge = ({ status }: { status: string }) => {
  const s = status?.toLowerCase() || 'pending';
  const map: Record<string, { bg: string; text: string; label: string }> = {
    pending:         { bg: '#BA7517', text: '#BA7517', label: 'Pending' },
    verified:        { bg: '#1D9E75', text: '#1D9E75', label: '✓ Verified' },
    rejected:        { bg: '#D85A30', text: '#D85A30', label: 'Rejected' },
    needs_more_info: { bg: '#534AB7', text: '#534AB7', label: 'Needs Info' },
    suspended:       { bg: '#D85A30', text: '#D85A30', label: 'Suspended' },
    revoked:         { bg: '#D85A30', text: '#D85A30', label: 'Revoked' },
    released:        { bg: '#666666', text: '#666666', label: 'Released' },
  };
  const style = map[s] || { bg: '#666', text: '#666', label: s };
  return (
    <span className="px-3 py-1 rounded text-sm font-bold tracking-wide" style={{ backgroundColor: `${style.bg}15`, color: style.text }}>
      {style.label}
    </span>
  );
};

const StatusBadge = ({ status }: { status: string }) => {
  const normalized = status?.toLowerCase() || 'pending';
  if (normalized === 'pending') return <span className="px-3 py-1 rounded bg-[#BA7517]/10 text-[#BA7517] text-sm font-bold tracking-wide">Pending</span>;
  if (normalized === 'verified') return <span className="px-3 py-1 rounded bg-[#1D9E75]/10 text-[#1D9E75] text-sm font-bold tracking-wide">✓ Verified</span>;
  if (normalized === 'rejected') return <span className="px-3 py-1 rounded bg-[#D85A30]/10 text-[#D85A30] text-sm font-bold tracking-wide">Rejected</span>;
  return <span className="px-3 py-1 rounded bg-gray-100 text-gray-600 text-sm font-bold tracking-wide uppercase">{normalized}</span>;
};

// ── Main Component ──────────────────────────────────────────────────────────

export default function Authors() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'claims' | 'pending' | 'all' | 'book-requests'>('claims');
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'claims' || tab === 'book-requests') {
      setActiveTab(tab);
      setClaimStatusFilter('pending');
      setBookReqStatusFilter('pending');
      setBookReqPage(1);
      setBookReqTypeFilter('');
      setClaimSearch('');
      setPage(1);
    }
  }, [searchParams]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({ show: false, message: '', type: 'success' });

  // Legacy tabs state
  const [pendingAuthors, setPendingAuthors] = useState<Author[]>([]);
  const [allAuthorsData, setAllAuthorsData] = useState<AllAuthorsResponse>({ total: 0, page: 1, limit: 20, authors: [] });
  const [rejectModal, setRejectModal] = useState<{ show: boolean; id: string | null }>({ show: false, id: null });

  // Claims tab state
  const [claimsData, setClaimsData] = useState<ClaimsResponse>({ claims: [], total: 0, page: 1, limit: 20 });
  const [claimStatusFilter, setClaimStatusFilter] = useState('pending');
  const [claimSearch, setClaimSearch] = useState('');
  // Search is submitted on Enter, not on each keystroke. Other reads use its latest text.
  const claimSearchRef = useRef(claimSearch);
  useEffect(() => { claimSearchRef.current = claimSearch; }, [claimSearch]);
  const [selectedClaim, setSelectedClaim] = useState<ClaimDetail | null>(null);
  const [claimDetailLoading, setClaimDetailLoading] = useState(false);
  const [claimModal, setClaimModal] = useState<{ show: boolean; action: string; claimId: string | null }>({ show: false, action: '', claimId: null });

  // Book Requests tab state
  const [bookRequests, setBookRequests] = useState<any[]>([]);
  const [bookReqTotal, setBookReqTotal] = useState(0);
  const [bookReqPage, setBookReqPage] = useState(1);
  const [bookReqStatusFilter, setBookReqStatusFilter] = useState('pending');
  const [bookReqTypeFilter, setBookReqTypeFilter] = useState('');
  const [bookReqLoading, setBookReqLoading] = useState(false);

  // ISBN evidence preview (Step 12 endpoint — on-demand, read-only, reference only)
  const [isbnPreviews, setIsbnPreviews] = useState<Record<string, any>>({});
  const [isbnPreviewLoading, setIsbnPreviewLoading] = useState<Record<string, boolean>>({});

  const showToast = useCallback((message: string, type: 'success' | 'error') => setToast({ show: true, message, type }), []);

  // ── Legacy fetchers ─────────────────────────────────────────────────────

  const fetchPending = useCallback(async () => {
    try { setLoading(true); setError(null);
      const res = await adminApi.get('/admin/authors/pending');
      setPendingAuthors(Array.isArray(res.data) ? res.data : []);
    } catch (err: any) { setError(err.response?.data?.detail || 'Failed to fetch pending authors');
    } finally { setLoading(false); }
  }, []);

  const fetchAll = useCallback(async (currentPage: number) => {
    try { setLoading(true); setError(null);
      const res = await adminApi.get(`/admin/authors/all?page=${currentPage}&limit=20`);
      setAllAuthorsData(res.data?.authors ? res.data : { total: 0, page: 1, limit: 20, authors: Array.isArray(res.data) ? res.data : [] });
    } catch (err: any) { setError(err.response?.data?.detail || 'Failed to fetch all authors');
    } finally { setLoading(false); }
  }, []);

  // ── Claims fetchers ─────────────────────────────────────────────────────

  const fetchClaims = useCallback(async (currentPage: number) => {
    try { setLoading(true); setError(null);
      const params = new URLSearchParams({ status_filter: claimStatusFilter, page: String(currentPage), limit: '20' });
      if (claimSearchRef.current.trim()) params.set('search', claimSearchRef.current.trim());
      const res = await adminApi.get(`/admin/claims?${params}`);
      setClaimsData(res.data || { claims: [], total: 0, page: currentPage, limit: 20 });
    } catch (err: any) { setError(err.response?.data?.detail || 'Failed to fetch claims');
    } finally { setLoading(false); }
  }, [claimStatusFilter]);

  const fetchClaimDetail = async (claimId: string) => {
    try { setClaimDetailLoading(true);
      const res = await adminApi.get(`/admin/claims/${claimId}`);
      setSelectedClaim(res.data);
    } catch (err: any) { showToast(err.response?.data?.detail || 'Failed to load claim detail', 'error');
    } finally { setClaimDetailLoading(false); }
  };

  // ── Claim actions ───────────────────────────────────────────────────────

  const handleClaimApprove = async (claimId: string) => {
    try {
      const claim = claimsData.claims.find(c => c.claim_id === claimId);
      if (claim?.is_new_author_request) {
        await adminApi.post(`/admin/claims/${claimId}/create-and-approve`, { reason: '' });
        showToast('Author created and claim approved', 'success');
      } else {
        await adminApi.post(`/admin/claims/${claimId}/approve`, { reason: '' });
        showToast('Claim approved', 'success');
      }
      setSelectedClaim(null);
      fetchClaims(page);
    } catch (err: any) { showToast(err.response?.data?.detail || 'Failed to approve claim', 'error'); }
  };

  const handleClaimAction = async (reason: string) => {
    if (!claimModal.claimId) return;
    const { action, claimId } = claimModal;
    try {
      if (action === 'reject') {
        await adminApi.post(`/admin/claims/${claimId}/reject`, { reason });
        showToast('Claim rejected', 'success');
      } else if (action === 'request-info') {
        await adminApi.post(`/admin/claims/${claimId}/request-info`, { reason });
        showToast('Information requested', 'success');
      } else if (action === 'suspend') {
        await adminApi.post(`/admin/claims/${claimId}/suspend`, { reason });
        showToast('Claim suspended', 'success');
      } else if (action === 'revoke') {
        await adminApi.post(`/admin/claims/${claimId}/revoke`, { reason });
        showToast('Claim revoked', 'success');
      } else if (action === 'reinstate') {
        await adminApi.post(`/admin/claims/${claimId}/reinstate`, { reason });
        showToast('Claim reinstated', 'success');
      }
      setClaimModal({ show: false, action: '', claimId: null });
      setSelectedClaim(null);
      fetchClaims(page);
    } catch (err: any) {
      showToast(err.response?.data?.detail || `Failed to ${action}`, 'error');
      setClaimModal({ show: false, action: '', claimId: null });
    }
  };

  // ── Legacy actions ──────────────────────────────────────────────────────

  const handleVerify = async (id: string) => {
    try {
      await adminApi.post(`/admin/authors/${id}/verify`);
      showToast('Author verified successfully', 'success');
      activeTab === 'pending' ? fetchPending() : fetchAll(page);
    } catch (err: any) { showToast(err.response?.data?.detail || 'Failed to verify author', 'error'); }
  };

  const handleReject = async (reason: string) => {
    if (!rejectModal.id) return;
    try {
      await adminApi.post(`/admin/authors/${rejectModal.id}/reject`, { reason });
      showToast('Author claim rejected successfully', 'success');
      setRejectModal({ show: false, id: null });
      activeTab === 'pending' ? fetchPending() : fetchAll(page);
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Failed to reject author', 'error');
      setRejectModal({ show: false, id: null });
    }
  };

  // ── Book Requests fetcher ─────────────────────────────────────────────

  const fetchBookRequests = useCallback(async (p: number) => {
    try {
      setBookReqLoading(true);
      const params = new URLSearchParams({ page: String(p), per_page: '20' });
      if (bookReqStatusFilter) params.set('status_filter', bookReqStatusFilter);
      if (bookReqTypeFilter) params.set('request_type', bookReqTypeFilter);
      const res = await adminApi.get(`/admin/book-requests?${params.toString()}`);
      setBookRequests(res.data.items || []);
      setBookReqTotal(res.data.total || 0);
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Failed to load book requests', 'error');
    } finally {
      setBookReqLoading(false);
    }
  }, [bookReqStatusFilter, bookReqTypeFilter, showToast]);

  // ── Effects ─────────────────────────────────────────────────────────────

  useEffect(() => {
    setPage(1);
    setSelectedClaim(null);
  }, [activeTab, claimStatusFilter]);

  useEffect(() => {
    if (activeTab === 'claims') void fetchClaims(page);
    else if (activeTab === 'pending') void fetchPending();
    else if (activeTab === 'all') void fetchAll(page);
  }, [activeTab, page, fetchClaims, fetchPending, fetchAll]);

  useEffect(() => {
    if (activeTab === 'book-requests') void fetchBookRequests(bookReqPage);
  }, [activeTab, bookReqPage, fetchBookRequests]);

  const handleBookReqAction = async (requestId: string, action: 'approve' | 'reject') => {
    if (action === 'reject') {
      const reason = prompt('Rejection reason:');
      if (!reason || !reason.trim()) return;
      try {
        await adminApi.post(`/admin/book-requests/${requestId}/reject`, { reason: reason.trim() });
        showToast('Request rejected.', 'success');
        fetchBookRequests(bookReqPage);
      } catch (err: any) {
        showToast(err.response?.data?.detail || 'Failed to reject', 'error');
      }
    } else {
      if (!window.confirm('Approve this book request? (You should manually link/unlink the book in Master Data Directory after approving.)')) return;
      try {
        await adminApi.post(`/admin/book-requests/${requestId}/approve`, { reason: '' });
        showToast('Request approved.', 'success');
        fetchBookRequests(bookReqPage);
      } catch (err: any) {
        showToast(err.response?.data?.detail || 'Failed to approve', 'error');
      }
    }
  };

  // On-demand ISBN evidence check (Step 12 endpoint). Read-only reference info
  // for the reviewing admin — never links or creates anything. Fetched lazily
  // per-row on click, not automatically for every row (avoids firing an
  // external Google Books lookup for every request on every page load).
  const fetchIsbnPreview = async (requestId: string) => {
    setIsbnPreviewLoading(prev => ({ ...prev, [requestId]: true }));
    try {
      const res = await adminApi.get(`/admin/book-requests/${requestId}/isbn-preview`);
      setIsbnPreviews(prev => ({ ...prev, [requestId]: res.data }));
    } catch (err: any) {
      setIsbnPreviews(prev => ({ ...prev, [requestId]: { error: err.response?.data?.detail || 'ISBN preview failed' } }));
    } finally {
      setIsbnPreviewLoading(prev => ({ ...prev, [requestId]: false }));
    }
  };

  // ── Render: Claims Detail Panel ─────────────────────────────────────────

  const renderClaimDetail = () => {
    if (!selectedClaim) return null;
    const c = selectedClaim;
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 mb-6">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-xl font-bold text-[#085041]">Claim Detail</h2>
          <button onClick={() => setSelectedClaim(null)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
        </div>

        {claimDetailLoading ? (
          <div className="py-8 text-center text-gray-500">Loading...</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Left: Claim + Claimer */}
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Claimer</h3>
                <p className="font-medium text-[#085041]">{c.claimer.name || 'Unknown'}</p>
                <p className="text-sm text-gray-500">{c.claimer.email}</p>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Claim Info</h3>
                <div className="space-y-1 text-sm">
                  <p><span className="text-gray-500">Status:</span> <ClaimStatusBadge status={c.status} /></p>
                  <p><span className="text-gray-500">Type:</span> {c.is_new_author_request ? 'New Author Request' : 'Existing Author Claim'}</p>
                  <p><span className="text-gray-500">Role:</span> {c.role}</p>
                  {c.pen_name && <p><span className="text-gray-500">Pen Name:</span> {c.pen_name}</p>}
                  <p><span className="text-gray-500">Submitted:</span> {c.submitted_at ? new Date(c.submitted_at).toLocaleString() : '—'}</p>
                  {c.reviewed_at && <p><span className="text-gray-500">Reviewed:</span> {new Date(c.reviewed_at).toLocaleString()}</p>}
                  {c.review_reason && <p><span className="text-gray-500">Review Note:</span> {c.review_reason}</p>}
                </div>
              </div>

              {/* Evidence */}
              {c.evidence && Object.keys(c.evidence).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Evidence</h3>
                  <div className="bg-gray-50 rounded p-3 text-sm space-y-1">
                    {Object.entries(c.evidence).map(([key, val]) => (
                      <p key={key}><span className="text-gray-500 capitalize">{key.replace(/_/g, ' ')}:</span> {String(val)}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right: Author + Warnings */}
            <div className="space-y-4">
              {c.is_new_author_request ? (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Proposed Author</h3>
                  <p className="font-medium text-[#085041]">{c.proposed_name}</p>
                  {c.proposed_name_sinhala && <p className="text-gray-600">{c.proposed_name_sinhala}</p>}
                </div>
              ) : c.author ? (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Canonical Author</h3>
                  <div className="flex items-center gap-3">
                    {c.author.avatar_url ? (
                      <img src={c.author.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[#085041] text-white flex items-center justify-center text-lg font-bold">
                        {c.author.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-[#085041]">{c.author.name}</p>
                      {c.author.name_sinhala && <p className="text-sm text-gray-500">{c.author.name_sinhala}</p>}
                      <p className="text-xs text-gray-400">{c.author.book_count} book(s) linked</p>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Similar Authors (for new-author requests) */}
              {c.similar_authors && c.similar_authors.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Similar Existing Authors</h3>
                  <div className="space-y-2">
                    {c.similar_authors.map(sa => (
                      <div key={sa.id} className="bg-gray-50 rounded p-2 text-sm flex justify-between items-center">
                        <div>
                          <span className="font-medium">{sa.name}</span>
                          {sa.name_sinhala && <span className="text-gray-500 ml-2">{sa.name_sinhala}</span>}
                        </div>
                        {sa.is_verified && <span className="text-[#1D9E75] text-xs font-bold">Verified</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Warnings */}
              {c.warnings && c.warnings.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Warnings</h3>
                  <div className="space-y-2">
                    {c.warnings.map((w, i) => (
                      <div key={i} className="bg-[#BA7517]/10 text-[#BA7517] p-2 rounded text-sm border-l-2 border-[#BA7517]">
                        {w}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              {c.status === 'pending' && (
                <div className="pt-4 border-t space-y-2">
                  <button
                    onClick={() => handleClaimApprove(c.claim_id)}
                    className="w-full px-4 py-2 bg-[#1D9E75] text-white rounded font-medium hover:opacity-90"
                  >
                    {c.is_new_author_request ? 'Create Author & Approve' : '✓ Approve Claim'}
                  </button>
                  <button
                    onClick={() => setClaimModal({ show: true, action: 'request-info', claimId: c.claim_id })}
                    className="w-full px-4 py-2 bg-[#534AB7] text-white rounded font-medium hover:opacity-90"
                  >
                    Request More Info
                  </button>
                  <button
                    onClick={() => setClaimModal({ show: true, action: 'reject', claimId: c.claim_id })}
                    className="w-full px-4 py-2 border border-[#D85A30] text-[#D85A30] rounded font-medium hover:bg-[#D85A30] hover:text-white transition-colors"
                  >
                    Reject
                  </button>
                </div>
              )}
              {c.status === 'needs_more_info' && (
                <div className="pt-4 border-t space-y-2">
                  <button
                    onClick={() => handleClaimApprove(c.claim_id)}
                    className="w-full px-4 py-2 bg-[#1D9E75] text-white rounded font-medium hover:opacity-90"
                  >
                    {c.is_new_author_request ? 'Create Author & Approve' : '✓ Approve Claim'}
                  </button>
                  <button
                    onClick={() => setClaimModal({ show: true, action: 'reject', claimId: c.claim_id })}
                    className="w-full px-4 py-2 border border-[#D85A30] text-[#D85A30] rounded font-medium hover:bg-[#D85A30] hover:text-white transition-colors"
                  >
                    Reject
                  </button>
                </div>
              )}
              {c.status === 'verified' && (
                <div className="pt-4 border-t space-y-2">
                  <button
                    onClick={() => setClaimModal({ show: true, action: 'suspend', claimId: c.claim_id })}
                    className="w-full px-4 py-2 bg-[#BA7517] text-white rounded font-medium hover:opacity-90"
                  >
                    ⏸ Suspend Author
                  </button>
                  <button
                    onClick={() => setClaimModal({ show: true, action: 'revoke', claimId: c.claim_id })}
                    className="w-full px-4 py-2 border border-[#D85A30] text-[#D85A30] rounded font-medium hover:bg-[#D85A30] hover:text-white transition-colors"
                  >
                    ✗ Revoke Claim
                  </button>
                </div>
              )}
              {c.status === 'suspended' && (
                <div className="pt-4 border-t space-y-2">
                  <button
                    onClick={() => setClaimModal({ show: true, action: 'reinstate', claimId: c.claim_id })}
                    className="w-full px-4 py-2 bg-[#1D9E75] text-white rounded font-medium hover:opacity-90"
                  >
                    ✓ Reinstate Author
                  </button>
                  <button
                    onClick={() => setClaimModal({ show: true, action: 'revoke', claimId: c.claim_id })}
                    className="w-full px-4 py-2 border border-[#D85A30] text-[#D85A30] rounded font-medium hover:bg-[#D85A30] hover:text-white transition-colors"
                  >
                    ✗ Revoke Claim
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Render: Claims List ─────────────────────────────────────────────────

  const renderClaims = () => {
    const list = claimsData.claims || [];

    return (
      <>
        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          {['pending', 'needs_more_info', 'verified', 'rejected', 'suspended', 'revoked', 'released', 'all'].map(s => (
            <button
              key={s}
              onClick={() => setClaimStatusFilter(s)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                claimStatusFilter === s ? 'bg-[#085041] text-white shadow' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              {s === 'needs_more_info' ? 'Needs Info' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search by name, email, or author..."
            value={claimSearch}
            onChange={(e) => setClaimSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); fetchClaims(1); } }}
            className="w-full md:w-96 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#1D9E75]"
          />
        </div>

        {/* Detail panel */}
        {renderClaimDetail()}

        {/* List */}
        {loading && list.length === 0 ? (
          <div className="py-12 text-center text-gray-500 bg-white rounded-lg shadow-sm">Loading claims...</div>
        ) : list.length === 0 ? (
          <div className="py-12 text-center text-gray-500 bg-white rounded-lg shadow-sm flex flex-col items-center">
            <span className="text-4xl mb-4">📋</span>
            No claims found.
          </div>
        ) : (
          <div className="grid gap-4">
            {list.map(claim => (
              <div
                key={claim.claim_id}
                onClick={() => fetchClaimDetail(claim.claim_id)}
                className={`bg-white rounded-lg shadow-sm border p-4 cursor-pointer hover:border-[#1D9E75] transition-colors ${
                  selectedClaim?.claim_id === claim.claim_id ? 'border-[#1D9E75] border-2' : 'border-gray-100'
                }`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="flex-grow min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-bold text-[#085041] truncate">{claim.author_name || claim.author_name_sinhala || 'Unknown'}</h3>
                      <ClaimStatusBadge status={claim.status} />
                      {claim.is_new_author_request && (
                        <span className="px-2 py-0.5 rounded bg-[#534AB7]/10 text-[#534AB7] text-xs font-bold">NEW</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      Claimed by: {claim.claimer_name || 'Unknown'} ({claim.claimer_email || '—'})
                    </p>
                    {claim.submitted_at && (
                      <p className="text-xs text-gray-400 mt-1">{new Date(claim.submitted_at).toLocaleString()}</p>
                    )}
                  </div>
                  <div className="flex-shrink-0 text-sm text-gray-400">
                    {claim.role}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {claimsData.total > 0 && Math.ceil(claimsData.total / 20) > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            <button disabled={page === 1} onClick={() => setPage(page - 1)} className="px-4 py-2 bg-white border border-gray-200 rounded text-[#085041] disabled:opacity-50">Previous</button>
            <span className="px-4 py-2 bg-white border border-gray-200 rounded font-medium text-gray-700">Page {page} of {Math.ceil(claimsData.total / 20)}</span>
            <button disabled={page >= Math.ceil(claimsData.total / 20)} onClick={() => setPage(page + 1)} className="px-4 py-2 bg-white border border-gray-200 rounded text-[#085041] disabled:opacity-50">Next</button>
          </div>
        )}
      </>
    );
  };

  // ── Render: Legacy Authors ──────────────────────────────────────────────

  // ── Render: Book Requests ─────────────────────────────────────────────

  const renderBookRequests = () => {
    const statusColors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
    };
    const typeLabels: Record<string, string> = {
      link_book: '🔗 Missing Book',
      wrong_book: '⚠️ Wrong Book',
    };

    return (
      <div>
        {/* Filters */}
        <div className="flex gap-3 mb-4 flex-wrap">
          {['pending', 'approved', 'rejected', ''].map(s => (
            <button
              key={s || 'all'}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                bookReqStatusFilter === s ? 'bg-[#085041] text-white' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
              onClick={() => { setBookReqStatusFilter(s); setBookReqPage(1); }}
            >
              {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
            </button>
          ))}
          <span className="border-l border-gray-300 mx-1" />
          {['', 'link_book', 'wrong_book'].map(rt => (
            <button
              key={rt || 'all-type'}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                bookReqTypeFilter === rt ? 'bg-[#BA7517] text-white' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
              onClick={() => { setBookReqTypeFilter(rt); setBookReqPage(1); }}
            >
              {rt ? typeLabels[rt] : 'All Types'}
            </button>
          ))}
        </div>

        {bookReqLoading ? (
          <div className="py-12 text-center text-gray-500 bg-white rounded-lg shadow-sm">Loading book requests...</div>
        ) : bookRequests.length === 0 ? (
          <div className="py-12 text-center bg-white rounded-lg shadow-sm">
            <div className="text-4xl mb-3">📚</div>
            <div className="text-gray-500">No book requests found.</div>
          </div>
        ) : (
          <div className="space-y-3">
            {bookRequests.map((r: any) => (
              <div key={r.request_id} className="bg-white rounded-lg shadow-sm border border-gray-100 p-5">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex gap-2 items-center">
                    <span className={`px-3 py-1 rounded text-xs font-bold ${r.request_type === 'link_book' ? 'bg-[#E1F5EE] text-[#085041]' : 'bg-red-50 text-red-700'}`}>
                      {typeLabels[r.request_type] || r.request_type}
                    </span>
                    <span className={`px-3 py-1 rounded text-xs font-bold ${statusColors[r.status] || ''}`}>
                      {r.status.toUpperCase()}
                    </span>
                  </div>
                  <span className="text-xs text-gray-400">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</span>
                </div>

                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm mb-3">
                  <div><span className="text-gray-500">Author:</span> <strong>{r.author_name || '-'}</strong></div>
                  <div><span className="text-gray-500">Requested by:</span> {r.user_name || r.user_id}</div>
                  <div><span className="text-gray-500">Book:</span> {r.book_name || '-'}</div>
                  {r.current_author_name && (
                    <div><span className="text-gray-500">Current Author:</span> {r.current_author_name}</div>
                  )}
                  {r.isbn && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-gray-500">ISBN:</span> {r.isbn}
                      <button
                        onClick={() => fetchIsbnPreview(r.request_id)}
                        disabled={!!isbnPreviewLoading[r.request_id]}
                        className="text-xs px-2 py-0.5 rounded border border-[#534AB7] text-[#534AB7] hover:bg-[#534AB7] hover:text-white transition-colors disabled:opacity-50"
                      >
                        {isbnPreviewLoading[r.request_id] ? 'Checking…' : '🔍 Check ISBN'}
                      </button>
                    </div>
                  )}
                  {r.publisher && <div><span className="text-gray-500">Publisher:</span> {r.publisher}</div>}
                </div>

                {isbnPreviews[r.request_id] && (
                  <div className="bg-[#E1F5EE] rounded p-3 text-sm mb-3 space-y-1">
                    {isbnPreviews[r.request_id].error ? (
                      <p className="text-[#D85A30]">{isbnPreviews[r.request_id].error}</p>
                    ) : !isbnPreviews[r.request_id].valid ? (
                      <p className="text-gray-600">ISBN missing or checksum invalid — evidence unverifiable, review manually.</p>
                    ) : (
                      <>
                        <p className="font-medium text-[#085041]">ISBN {isbnPreviews[r.request_id].normalized_isbn} — checksum valid</p>
                        {isbnPreviews[r.request_id].existing_match ? (
                          <p>⚠ Already in Readora catalog: <strong>{isbnPreviews[r.request_id].existing_match.title}</strong> ({isbnPreviews[r.request_id].existing_match.source})</p>
                        ) : (
                          <p className="text-gray-500">No existing Readora catalog match for this ISBN.</p>
                        )}
                        {isbnPreviews[r.request_id].google_books ? (
                          <p className="text-gray-600">Google Books: {isbnPreviews[r.request_id].google_books.title || '—'}{isbnPreviews[r.request_id].google_books.author ? ` by ${isbnPreviews[r.request_id].google_books.author}` : ''}</p>
                        ) : (
                          <p className="text-gray-400 text-xs">Google Books preview unavailable.</p>
                        )}
                        <p className="text-xs text-gray-400 italic">Reference only — does not link or create anything.</p>
                      </>
                    )}
                  </div>
                )}

                {r.notes && (
                  <div className="bg-gray-50 rounded p-3 text-sm text-gray-700 mb-3">
                    <span className="font-medium text-gray-500">Notes:</span> {r.notes}
                  </div>
                )}

                {r.review_reason && r.status !== 'pending' && (
                  <div className="bg-gray-50 rounded p-3 text-sm mb-3">
                    <span className="font-medium text-gray-500">Admin reason:</span> {r.review_reason}
                  </div>
                )}

                {r.status === 'pending' && (
                  <div className="flex gap-3 mt-2">
                    <button
                      onClick={() => handleBookReqAction(r.request_id, 'approve')}
                      className="px-4 py-2 bg-[#1D9E75] text-white rounded-lg text-sm font-bold hover:bg-opacity-90"
                    >
                      ✓ Approve
                    </button>
                    <button
                      onClick={() => handleBookReqAction(r.request_id, 'reject')}
                      className="px-4 py-2 bg-[#D85A30] text-white rounded-lg text-sm font-bold hover:bg-opacity-90"
                    >
                      ✗ Reject
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {bookReqTotal > 20 && (
          <div className="flex justify-center gap-2 mt-6">
            <button disabled={bookReqPage === 1} onClick={() => setBookReqPage(bookReqPage - 1)} className="px-4 py-2 bg-white border border-gray-200 rounded text-[#085041] disabled:opacity-50">Previous</button>
            <span className="px-4 py-2 bg-white border border-gray-200 rounded font-medium text-gray-700">Page {bookReqPage} of {Math.ceil(bookReqTotal / 20)}</span>
            <button disabled={bookReqPage >= Math.ceil(bookReqTotal / 20)} onClick={() => setBookReqPage(bookReqPage + 1)} className="px-4 py-2 bg-white border border-gray-200 rounded text-[#085041] disabled:opacity-50">Next</button>
          </div>
        )}
      </div>
    );
  };

  const renderLegacyData = () => {
    const list = activeTab === 'pending' ? pendingAuthors : (allAuthorsData.authors || []);

    if (loading && list.length === 0) {
      return <div className="py-12 text-center text-gray-500 bg-white rounded-lg shadow-sm">Loading authors...</div>;
    }
    if (list.length === 0) {
      return (
        <div className="py-12 text-center text-gray-500 bg-white rounded-lg shadow-sm flex flex-col items-center">
          <span className="text-4xl mb-4">📋</span>No authors found in this category.
        </div>
      );
    }

    return (
      <div className="grid gap-6">
        {list.map((author) => (
          <div key={author.id} className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 flex flex-col md:flex-row gap-6 items-start md:items-center">
            <div className="flex-shrink-0">
              {author.avatar_url ? (
                <img src={author.avatar_url} alt={author.name} className="w-20 h-20 rounded-full object-cover border-2 border-gray-100" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-[#085041] text-white flex items-center justify-center text-3xl font-bold border-2 border-gray-100">
                  {author.name ? author.name.charAt(0).toUpperCase() : '?'}
                </div>
              )}
            </div>
            <div className="flex-grow flex flex-col gap-2 min-w-0">
              <h2 className="text-xl font-bold text-[#085041] truncate">{author.name}</h2>
              <p className="text-gray-600 text-sm">
                {author.bio && author.bio.length > 100 ? author.bio.substring(0, 100) + '...' : (author.bio || 'No bio provided.')}
              </p>
              {author.is_claimed && (
                <div className="mt-2 bg-[#BA7517]/10 text-gray-800 p-3 rounded text-sm border-l-2 border-[#BA7517]">
                  <span className="font-semibold block mb-1">Claimed By:</span>
                  {author.claimer_name} <span className="text-gray-500">({author.claimer_email})</span>
                </div>
              )}
            </div>
            <div className="flex flex-col items-start md:items-end gap-4 min-w-[140px] flex-shrink-0">
              <StatusBadge status={author.verification_status} />
              {activeTab === 'pending' && (
                <div className="flex flex-col w-full gap-2 mt-2">
                  <button onClick={() => handleVerify(author.id)} className="w-full text-center px-4 py-2 bg-[#1D9E75] text-white rounded font-medium hover:bg-opacity-90">✓ Verify</button>
                  <button onClick={() => setRejectModal({ show: true, id: author.id })} className="w-full text-center px-4 py-2 border border-[#D85A30] text-[#D85A30] rounded font-medium hover:bg-[#D85A30] hover:text-white transition-colors">✗ Reject</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ── Main Return ─────────────────────────────────────────────────────────

  return (
    <div className="bg-[#F1EFE8] min-h-screen p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-[#085041] mb-6">Author Management</h1>

        {error && (
          <div className="bg-[#D85A30]/10 border border-[#D85A30] text-[#D85A30] px-4 py-3 rounded mb-6">{error}</div>
        )}

        {/* Tab bar */}
        <div className="flex gap-4 mb-6">
          <button
            className={`px-6 py-3 rounded-lg font-bold transition-colors ${activeTab === 'claims' ? 'bg-[#085041] text-white shadow' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            onClick={() => setActiveTab('claims')}
          >
            Author Claims
          </button>
          <button
            className={`px-6 py-3 rounded-lg font-bold transition-colors ${activeTab === 'pending' ? 'bg-[#085041] text-white shadow' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            onClick={() => setActiveTab('pending')}
          >
            Legacy Pending
          </button>
          <button
            className={`px-6 py-3 rounded-lg font-bold transition-colors ${activeTab === 'all' ? 'bg-[#085041] text-white shadow' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            onClick={() => setActiveTab('all')}
          >
            All Authors
          </button>
          <button
            className={`px-6 py-3 rounded-lg font-bold transition-colors ${activeTab === 'book-requests' ? 'bg-[#BA7517] text-white shadow' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            onClick={() => setActiveTab('book-requests')}
          >
            Book Requests
          </button>
        </div>

        {/* Tab content */}
        {activeTab === 'claims' ? renderClaims() : activeTab === 'book-requests' ? renderBookRequests() : renderLegacyData()}

        {/* Legacy pagination */}
        {activeTab === 'all' && allAuthorsData.total > 0 && Math.ceil((allAuthorsData.total ?? 0) / (allAuthorsData.limit || 20)) > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            <button disabled={page === 1} onClick={() => setPage(page - 1)} className="px-4 py-2 bg-white border border-gray-200 rounded text-[#085041] disabled:opacity-50">Previous</button>
            <span className="px-4 py-2 bg-white border border-gray-200 rounded font-medium text-gray-700">Page {page} of {Math.ceil((allAuthorsData.total ?? 0) / (allAuthorsData.limit || 20))}</span>
            <button disabled={page >= Math.ceil((allAuthorsData.total ?? 0) / (allAuthorsData.limit || 20))} onClick={() => setPage(page + 1)} className="px-4 py-2 bg-white border border-gray-200 rounded text-[#085041] disabled:opacity-50">Next</button>
          </div>
        )}
      </div>

      <Toast message={toast.show ? toast.message : ''} type={toast.type} onClose={() => setToast(prev => ({ ...prev, show: false }))} />

      {/* Legacy reject modal */}
      <ReasonModal
        isOpen={rejectModal.show}
        onClose={() => setRejectModal({ show: false, id: null })}
        onConfirm={handleReject}
        title="Reject Author Claim"
        placeholder="Reason for rejection..."
        confirmLabel="Confirm Reject"
        confirmColor="#D85A30"
      />

      {/* Claims action modal */}
      <ReasonModal
        isOpen={claimModal.show}
        onClose={() => setClaimModal({ show: false, action: '', claimId: null })}
        onConfirm={handleClaimAction}
        title={
          claimModal.action === 'reject' ? 'Reject Claim'
          : claimModal.action === 'suspend' ? 'Suspend Author Claim'
          : claimModal.action === 'revoke' ? 'Revoke Author Claim'
          : claimModal.action === 'reinstate' ? 'Reinstate Author Claim'
          : 'Request More Information'
        }
        placeholder={
          claimModal.action === 'reject' ? 'Reason for rejection...'
          : claimModal.action === 'suspend' ? 'Reason for suspension...'
          : claimModal.action === 'revoke' ? 'Reason for revocation...'
          : claimModal.action === 'reinstate' ? 'Reason for reinstatement (optional)...'
          : 'What information is needed?'
        }
        confirmLabel={
          claimModal.action === 'reject' ? 'Confirm Reject'
          : claimModal.action === 'suspend' ? 'Confirm Suspend'
          : claimModal.action === 'revoke' ? 'Confirm Revoke'
          : claimModal.action === 'reinstate' ? 'Confirm Reinstate'
          : 'Send Request'
        }
        confirmColor={
          claimModal.action === 'reject' || claimModal.action === 'revoke' ? '#D85A30'
          : claimModal.action === 'suspend' ? '#BA7517'
          : claimModal.action === 'reinstate' ? '#1D9E75'
          : '#534AB7'
        }
      />
    </div>
  );
}
